import { z } from 'zod';
import { PaginationPageHasMoreSchema } from './shared/pagination.js';
import { TimeRangeSchema } from './time-range.js';

export const AllowedUserRoleSchema = z.enum(['free', 'pro', 'admin']);
export type AllowedUserRole = z.infer<typeof AllowedUserRoleSchema>;

export const CurrentUserDbInfoSchema = z.object({
  dbLabel: z.string().nullable(),
  dbMode: z.string().nullable(),
  isDev: z.boolean(),
});

export const CurrentUserResponseSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  isAdmin: z.boolean(),
  role: AllowedUserRoleSchema.nullable(),
  createdAt: z.date().nullable(),
  hasTwitterAccount: z.boolean(),
  twitterProfileId: z.string().nullable(),
  twitterUsername: z.string().nullable(),
  username: z.string().nullable(),
  name: z.string().nullable(),
  profileImageUrl: z.string().nullable(),
  dbInfo: CurrentUserDbInfoSchema.optional(),
});

export type CurrentUserResponse = z.infer<typeof CurrentUserResponseSchema>;

export const TweetStatsMonthlyPointSchema = z.object({
  month: z.string(),
  count: z.number().int(),
});

export type TweetStatsMonthlyPoint = z.infer<typeof TweetStatsMonthlyPointSchema>;

export const TweetStatsDailyPointSchema = z.object({
  date: z.string(),
  count: z.number().int(),
});

export type TweetStatsDailyPoint = z.infer<typeof TweetStatsDailyPointSchema>;

export const TweetStatsTopTweetSchema = z.object({
  id: z.string(),
  text: z.string(),
  createdAt: z.date(),
  totalEngagement: z.number(),
  likeCount: z.number(),
  retweetCount: z.number(),
  replyCount: z.number(),
  quoteCount: z.number(),
  bookmarkCount: z.number(),
});

export type TweetStatsTopTweet = z.infer<typeof TweetStatsTopTweetSchema>;

export const TweetStatsOverviewSchema = z.object({
  postingCadence: z.object({
    totalTweets: z.number().int(),
    averagePerMonth: z.number(),
    averagePerYear: z.number(),
    monthlyTrend: z.array(TweetStatsMonthlyPointSchema),
    busiestMonth: TweetStatsMonthlyPointSchema.nullable().optional(),
  }),
  engagement: z.object({
    averageTotalEngagement: z.number(),
    medianLikeCount: z.number(),
    topTweet: TweetStatsTopTweetSchema.nullable().optional(),
  }),
  formatMix: z.object({
    total: z.number().int(),
    original: z.number().int(),
    replies: z.number().int(),
    quotes: z.number().int(),
    retweets: z.number().int(),
    threads: z.number().int(),
    media: z.number().int(),
  }),
  consistency: z.object({
    longestStreakDays: z.number().int(),
    longestGapDays: z.number().int(),
    lastTweetAt: z.date().nullable(),
    recentDailyCounts: z.array(TweetStatsDailyPointSchema),
  }),
});

export type TweetStatsOverview = z.infer<typeof TweetStatsOverviewSchema>;

export const UserMentionsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(20),
  timeRange: TimeRangeSchema.default('24h'),
  cursor: z.string().optional(),
  q: z.string().optional(),
});

const UserMentionsPaginationSchema = PaginationPageHasMoreSchema.extend({
  cursor: z.string().nullable(),
  page: PaginationPageHasMoreSchema.shape.page.optional(),
  limit: PaginationPageHasMoreSchema.shape.limit.optional(),
  totalCount: PaginationPageHasMoreSchema.shape.totalCount,
});

const normalizeUserMentionsPagination = (
  pagination?: z.input<typeof UserMentionsPaginationSchema>
): z.output<typeof UserMentionsPaginationSchema> => ({
  cursor: pagination?.cursor ?? null,
  hasMore: pagination?.hasMore ?? false,
  totalCount: pagination?.totalCount ?? null,
  page: pagination?.page,
  limit: pagination?.limit,
});

export const UserMentionsResponseSchema = z
  .object({
    tweets: z.array(z.unknown()),
    pagination: UserMentionsPaginationSchema.optional(),
  })
  .transform((value) => ({
    tweets: value.tweets,
    pagination: normalizeUserMentionsPagination(value.pagination),
  }));

export type UserMentionsQuery = z.infer<typeof UserMentionsQuerySchema>;
export type UserMentionsResponse = z.infer<typeof UserMentionsResponseSchema>;

export const UserResolveRequestSchema = z
  .object({
    profileId: z.string().trim().optional(),
    username: z.string().trim().optional(),
    userId: z.string().trim().optional(),
  })
  .refine((value) => Boolean(value.profileId || value.username || value.userId), {
    message: 'profileId, userId, or username is required',
    path: ['profileId'],
  });

export const UserResolveMatchSchema = z.object({
  profileId: z.string(),
  username: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});

export const UserResolveResponseSchema = z.object({
  success: z.literal(true),
  data: UserResolveMatchSchema,
  message: z.string().nullable().optional(),
});

export type UserResolveRequest = z.infer<typeof UserResolveRequestSchema>;
export type UserResolveResponse = z.infer<typeof UserResolveResponseSchema>;

export const TimelineCacheOptionsSchema = z.object({
  timeRange: TimeRangeSchema.optional(),
  reportLength: z.string().optional(),
  model: z.string().optional(),
});

export const TimelineCacheEntrySchema = z.object({
  id: z.string(),
  options: TimelineCacheOptionsSchema.nullable(),
  createdAt: z.date(),
});

export const TimelineCacheStatsSchema = z.object({
  totalEntries: z.number().int(),
  timeRanges: z.array(TimeRangeSchema.nullable()),
  oldestEntry: z.number().nullable(),
  newestEntry: z.number().nullable(),
});

export const TimelineCacheInfoSchema = z.object({
  success: z.literal(true),
  stats: TimelineCacheStatsSchema,
  entries: z.array(TimelineCacheEntrySchema),
});

export const TimelineCacheClearResponseSchema = z.object({
  success: z.literal(true),
  cleared: z.number().int(),
});

export type TimelineCacheOptions = z.infer<typeof TimelineCacheOptionsSchema>;
export type TimelineCacheEntry = z.infer<typeof TimelineCacheEntrySchema>;
export type TimelineCacheInfo = z.infer<typeof TimelineCacheInfoSchema>;
export type TimelineCacheClearResponse = z.infer<typeof TimelineCacheClearResponseSchema>;

export const PromptProfileSectionSchema = z.object({
  id: z.string(),
  enabled: z.boolean(),
  heading: z.string().optional(),
  instructions: z.string().optional(),
});

export const PromptProfileSchema = z.object({
  version: z.number().int(),
  sections: z.array(PromptProfileSectionSchema),
});

export const QuotaCounterSchema = z.object({
  used: z.number().int(),
  limit: z.number().int().nullable(),
  remaining: z.number().int().nullable(),
  isUnlimited: z.boolean(),
});

export const AIQuotaSettingsSchema = z.object({
  preferredModel: z.string(),
  hasModelSelection: z.boolean(),
  quota: z
    .object({
      requestsLimit: z.number().int(),
      requestsUsed: z.number().int(),
      requestsRemaining: z.number().int(),
      isUnlimited: z.boolean(),
      billingCycleStart: z.date().nullable(),
      billingCycleEnd: z.date().nullable(),
    })
    .nullable(),
  isAdmin: z.boolean(),
  promptProfile: PromptProfileSchema,
});

export const AIAnalysisQuotaSchema = z.object({
  userAnalysis: QuotaCounterSchema,
  timelineAnalysis: QuotaCounterSchema,
  resetAt: z.date().nullable(),
});

export const AIQuotaSnapshotSchema = z.object({
  settings: AIQuotaSettingsSchema,
  analysisQuota: AIAnalysisQuotaSchema,
});

export const AIQuotaUpdateRequestSchema = z.object({
  preferredModel: z.string().min(1),
});

export const AIQuotaUpdateResponseSchema = AIQuotaSnapshotSchema.extend({
  success: z.literal(true),
});

export type AIQuotaSnapshotResponse = z.infer<typeof AIQuotaSnapshotSchema>;
export type AIQuotaUpdateResponse = z.infer<typeof AIQuotaUpdateResponseSchema>;

export const UserTweetsQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(200).default(20),
    cursor: z.string().optional(),
    q: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    sort: z.enum(['recent', 'popular']).optional(),
    media: z.enum(['true', 'false']).optional(),
    minEngagement: z.coerce.number().int().min(0).optional(),
  })
  .passthrough();

const UserTweetsPaginationSchema = PaginationPageHasMoreSchema.extend({
  cursor: z.string().nullable(),
  totalCount: z.number().int(),
});

const normalizeUserTweetsPagination = (
  pagination?: z.input<typeof UserTweetsPaginationSchema>
): z.output<typeof UserTweetsPaginationSchema> => ({
  cursor: pagination?.cursor ?? null,
  hasMore: pagination?.hasMore ?? false,
  totalCount: pagination?.totalCount ?? 0,
  page: pagination?.page ?? 1,
  limit: pagination?.limit ?? 20,
});

export const UserTweetsResponseSchema = z
  .object({
    tweets: z.array(z.unknown()),
    user: z.unknown().optional(),
    pagination: UserTweetsPaginationSchema.optional(),
    filters: z.record(z.string(), z.unknown()).optional(),
    missingMetadataUrls: z.array(z.string()).optional(),
  })
  .transform((value) => ({
    ...value,
    pagination: normalizeUserTweetsPagination(value.pagination),
  }));

export type UserTweetsQuery = z.infer<typeof UserTweetsQuerySchema>;
export type UserTweetsResponse = z.infer<typeof UserTweetsResponseSchema>;

export const BetaFeaturesSchema = z
  .object({
    websiteHoverPreview: z.boolean().optional(),
    arena: z.boolean().optional(),
    universalChat: z.boolean().optional(),
    conversationPrefetch: z.boolean().optional(),
    powerUser: z.boolean().optional(),
    algorithmicTimeline: z.boolean().optional(),
    debugLogging: z.boolean().optional(),
  })
  .catchall(z.boolean().optional())
  .partial();

export const UserBetaFeaturesResponseSchema = z.object({
  betaFeatures: BetaFeaturesSchema,
});

export const UserBetaFeaturesUpdateSchema = z.object({
  betaFeatures: BetaFeaturesSchema,
});

export type UserBetaFeaturesResponse = z.infer<typeof UserBetaFeaturesResponseSchema>;

// Profile Highlights
export const ProfileHighlightsInterestSchema = z.object({
  topic: z.string(),
  representativeTweetId: z.string().nullable(),
});

export const ProfileHighlightsBadgeSchema = z.object({
  id: z.string(),
  label: z.string(),
  reason: z.string(),
});

export const ProfileHighlightsReadySchema = z.object({
  status: z.literal('ready'),
  summary: z.string(),
  interests: z.array(ProfileHighlightsInterestSchema),
  badges: z.array(ProfileHighlightsBadgeSchema),
  insights: z.array(z.unknown()),
  recommendations: z.array(z.unknown()),
  generatedAt: z.string().nullable(),
  model: z.string().nullable(),
  tweetsAnalyzed: z.number().int().nullable(),
  username: z.string().nullable(),
  name: z.string().nullable(),
});

export const ProfileHighlightsPendingSchema = z.object({
  status: z.literal('pending'),
  message: z.string(),
});

export const ProfileHighlightsErrorSchema = z.object({
  error: z.string(),
});

export const ProfileHighlightsResponseSchema = z.union([
  ProfileHighlightsReadySchema,
  ProfileHighlightsPendingSchema,
  ProfileHighlightsErrorSchema,
]);

export type ProfileHighlightsResponse = z.infer<typeof ProfileHighlightsResponseSchema>;

// Features Refresh
export const FeaturesRefreshResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  userId: z.string(),
  currentFeatures: z.array(z.string()),
  isAdmin: z.boolean(),
});

export type FeaturesRefreshResponse = z.infer<typeof FeaturesRefreshResponseSchema>;

// AI Models
export const AIModelOptionSchema = z.object({
  id: z.string(),
  canonicalId: z.string(),
  providerModelId: z.string(),
  displayName: z.string(),
  label: z.string(),
  description: z.string(),
  provider: z.string(),
  tier: z.string(),
  config: z
    .object({
      maxTokens: z.number().optional(),
      temperature: z.number().optional(),
      providerOptions: z.record(z.string(), z.unknown()).optional(),
    })
    .nullable()
    .optional(),
});

export const AIModelsResponseSchema = z.object({
  success: z.boolean(),
  models: z.array(AIModelOptionSchema),
  defaultModelId: z.string(),
  isAdmin: z.boolean(),
  hasModelSelection: z.boolean(),
});

export type AIModelsResponse = z.infer<typeof AIModelsResponseSchema>;

// Twitter Image
export const TwitterImageQuerySchema = z.object({
  url: z.string().url(),
});

export const TwitterImageResponseSchema = z.object({
  imageUrl: z.string().url().nullable(),
});

export type TwitterImageQuery = z.infer<typeof TwitterImageQuerySchema>;
export type TwitterImageResponse = z.infer<typeof TwitterImageResponseSchema>;

// Notifications
export const WebPushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({
    p256dh: z.string(),
    auth: z.string(),
  }),
});

export const NotificationSubscribeResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type WebPushSubscription = z.infer<typeof WebPushSubscriptionSchema>;
export type NotificationSubscribeResponse = z.infer<typeof NotificationSubscribeResponseSchema>;

export const FeaturesRefreshRequestSchema = z.undefined();
export type FeaturesRefreshRequest = z.infer<typeof FeaturesRefreshRequestSchema>;

export const NotificationUnsubscribeRequestSchema = z.undefined();
export type NotificationUnsubscribeRequest = z.infer<typeof NotificationUnsubscribeRequestSchema>;
